import Vue from 'vue';
import Vuex from 'vuex';
import io from 'socket.io-client';
import storage from './storage';
import router from './router';
import eventHub from './mixins/eventHub';
import loginBus from './bus/loginBus';

import timingPlugin from './plugins/timingPlugin';
import storagePlugin from './plugins/storagePlugin';
import getSocketPlugin from './plugins/socketPlugin';

import i18n from './plugins/i18n'
import sounds, {Sounds} from "@/utils/sounds";
import {Call} from "@/data/Call";


Vue.use(Vuex);
const relativePath = (process.env.VUE_APP_SOCKET_PATH || '');
const socketUrl = process.env.VUE_APP_SOCKET_SERVER || 'http://localhost:3000';
const socket = io.connect(socketUrl, {path: relativePath});
const socketPlugin = getSocketPlugin(socket);

//const debug = process.env.NODE_ENV !== 'production'

let prevState = 'stop';
let prevEventLength = 0;
let prevSimId = null;

function getEventsLength(simulation, storeState) {
  return simulation.lists.waitingEvents ? simulation.lists.waitingEvents.filter((e) => {return e.properties.createdRelativeTimeNum.value < storeState.time.progress}).length : 0 +
  simulation.lists.assignedEvents ? simulation.lists.assignedEvents.filter((e) => {return e.properties.createdRelativeTimeNum.value < storeState.time.progress}).length : 0 +
  simulation.lists.cancelledEvents ? simulation.lists.cancelledEvents.filter((e) => {return e.properties.createdRelativeTimeNum.value < storeState.time.progress}).length : 0;
}

function getEmptySimulation() {
  return {
    id: null,
    entities: {},
    lists: {},
    properties: {},
    evaluation: {},
    pendingCalls: [],
    time: 0,
    progress: 0
  }
}

export default new Vuex.Store({
  state: {
    online: false,
    auth: {
      logged: false,
      user: null,
      simId: null,
      token: null,
      status: 'default'
    },
    time: {},
    simulation: getEmptySimulation(),
    simulationList: [],
    editMode: false,
    loaded: false,
    appState: 'loading',
    scenarioSelected: false,
    scenarioSelectedId: null,
    scenarioSelectedRole: null,
    version: process.env.VUE_APP_VERSION_VALUE,
    selected: {
      extra: {}, // Additional information for fine-tuning selection rendering
      entityId: null,
      events: [],
      crews: [],
      hospitals: [],
      bases: [],
      log: []
    },
    distances: null, // array for closest hospitals or fastest VSKs for distancesWidget as used in views/Map.vue
    locationSuggestions: null, // populated when typing into an address field, used to generate pins in mapComponent.vue
    editedScenarioRole: null, // user role selected for currently edited scenario
    mapEventHelper: null, // Leaflet has an issue when emitting events from circles - cant stop propagation, this helps
    call: null, // Object representing a call (active, or ringing, does not matter)
    autoCall: null, // Object representing a call with automatic answering machine
    micStream: null
  },
  mutations: {
    /**
     * Leaflet has an issue where circles do not properly stop propagation of events, it will ALWAYS reach the map
     * no matter what, and passing additional data with the event is probably impossible. So we use the mapEventHelper
     * variable and this mutation to pass the data and then handle in it the map event listener.
     * So the process is - when a circle event happens, set whatever you need in the circle event listener, and then in
     * the map event listener check the mapEventHelper for whatever you need, then clear the helper variable.
     * Example at mapComponent.vue - method openContextMenu(e) and eventMarker.vue - method openZoneContextMenu
     */
    setMapEventHelper(state, payload) {
      state.mapEventHelper = payload
    },
    setEditedScenarioRole(state, payload) {
      state.editedScenarioRole = payload
    },
    clearLocationSuggestions(state) {
      state.locationSuggestions = null
      localStorage.setItem('locationSuggestions', null)
    },
    setLocationSuggestions(state, payload) {
      state.locationSuggestions = payload
      localStorage.setItem('locationSuggestions', JSON.stringify(payload))
    },
    callApi(state, payload) {
      console.log(payload.name, payload.payload)
    },
    setEditMode(state, value) {
      state.editMode = value;
    },
    setSelected(state, payload) {
      state.selected[payload.property] = payload.value;
      state.selected.entityId = payload.value[0];
    },
    setSelectedExtra(state, payload = {}) {
      state.selected.extra = payload
    },
    clearSelected(state) {
      state.selected.events = null;
      state.selected.crews = null;
      state.selected.hospitals = null;
      state.selected.bases = null;
      state.selected.log = [];
      state.selected.entityId = null;
    },
    setLog(state, payload) {
      state.selected.log.push({
        time: payload.time,
        entityId: payload.entityId,
        text: payload.text
      })
      /*
      state.selected.log[0].entityId = payload.entityId;
      state.selected.log[0].time = payload.time;
      state.selected.log[0].text = payload.text;*/
    },
    clearLog(state) {
      state.selected.log = []; // clear log
    },
    clearCrews(state) {
      state.selected.crews = []; // clear selected crews
    },
    hideAllCrews(state) {
      state.selected.crews = []; // clear selected crews
    },
    hideAllBases(state) {
      state.selected.bases = []; // clear selected bases
    },
    hideAllEvents(state) {
      state.selected.events = [];
    },
    hideAllHospitals(state) {
      state.selected.hospitals = [];
    },
    refreshTime(state) {
      const now = new Date().getTime();

      const startTime = state.simulation.properties.startTime || 1604185200000;  // 1604185200000  = unix time for
                                                                                 // 1.11.2020
      const at = state.simulation.properties.at || 0;
      let progress = state.simulation.properties.progress || 0;
      const updated = state.simulation.properties.updated || 0;
      const simulationState = state.simulation.properties.state;

      const serverOffset = updated - at;

      const diff = now - serverOffset - at;
      if (simulationState === 'play') {
        progress += diff;
      }
      Vue.set(state.time, 'progress', progress);
      Vue.set(state.time, 'time', startTime + progress);
    },
    online(state) {
      state.online = true;
      eventHub.$emit('openMessageController', 'success', 'Online');
    },
    offline(state) {
      state.online = false;
      eventHub.$emit('openMessageController', 'error', 'Offline');
    },
    simulationData(state, data) {
      Vue.set(state, 'simulation', data);
      Vue.set(state.simulation.properties, 'updated', new Date().getTime());
      const simulation = state.simulation;

      // If we changed the simulation we are viewing, do not play the notification (set all events as 'read')
      if (prevSimId !== simulation.properties.simId) {
        prevSimId = simulation.properties.simId
        prevEventLength = getEventsLength(simulation, state);
      }

      if (prevState != simulation.properties.state) {
        if (simulation.properties.state == 'play') {
          eventHub.$emit('openMessageController', 'success', i18n.tc('Popups.scenarioRunning'));
          prevEventLength = getEventsLength(simulation, state);
        }
        else if (simulation.properties.state == 'stop') {
          prevEventLength = 0; // Make sure reset the variable after watched simulation is stopped
          eventHub.$emit('openMessageController', 'error', i18n.tc('Popups.scenarioTurnedOff'));
        }
        else if (simulation.properties.state == 'pause') {
          eventHub.$emit('openMessageController', 'error', i18n.tc('Popups.scenarioPaused'));
        }
        prevState = simulation.properties.state;
      }

      // Check for new events if the simulations is running
      if (simulation.properties.state === 'play') {
        let currentEventLength = getEventsLength(simulation, state);
        if (currentEventLength > prevEventLength) {
          prevEventLength = currentEventLength
          sounds.playSound(Sounds.notification)
        }
      }

      state.loaded = !!simulation.properties.simId;
      state.appState = (simulation.properties.simId) ? 'ready' : 'waiting';
      state.scenarioSelected = !!simulation.properties.simId;
      state.scenarioSelectedId = simulation.properties.simId;

      if (state.auth.user) {
        const simulationsRoles = state.auth.user.simulationsRoles || [];
        const simulationRole = simulationsRoles.filter(obj => {
          return obj.simulationId === simulation.properties.simId
        });
        state.scenarioSelectedRole = simulationRole.length == 1 ? simulationRole[0].roleId : null;
      }
    },
    sendCMD() {
      //plugin
    },
    authorizeCMD() {
      //plugin
    },
    setToken() {
      //plugin
    },
    resetToken() {
      //plugin
    },
    login(state) {
      Vue.set(state.auth, 'status', 'pending');
    },
    loggedId(state, res) {
      const {user, token} = res;
      Vue.set(state.auth, 'logged', true);
      Vue.set(state.auth, 'user', user);
      Vue.set(state.auth, 'token', token);
      Vue.set(state.auth, 'status', 'ok');
    },
    loginFailed(state, status) {
      Vue.set(state.auth, 'logged', false);
      Vue.set(state.auth, 'user', null);
      Vue.set(state.auth, 'token', null);
      Vue.set(state.auth, 'simId', null);
      Vue.set(state.auth, 'status', status);
    },
    logout(state) {
      Vue.set(state.auth, 'logged', false);
      Vue.set(state.auth, 'user', null);
      Vue.set(state.auth, 'token', null);
      Vue.set(state.auth, 'simId', null);
      Vue.set(state.auth, 'status', 'not authenticated');
      Vue.set(state, 'simulation', getEmptySimulation());
    },
    setSimulations(state, simulations) {
      Vue.set(state, 'simulationList', simulations);
    },
    setDistances(state, payload) {
      state.distances = payload
    },
    clearDistances(state) {
      state.distances = null
    },
    setUserCurrentOrgModules(state, payload) {
      if (state.auth && state.auth.user) {
        state.auth.user.currentOrgModules = payload
        localStorage.setItem('userCurrentOrgModules', payload) // Use local storage to synchronize change across tabs
      }
    },
    setUserCurrentOrgId(state, payload) {
      if (state.auth && state.auth.user) {
        state.auth.user.currentOrg = payload
        localStorage.setItem('userCurrentOrgId', payload) // Use local storage to synchronize change across tabs
      }
    },
    setSimulationRole(state, payload) {
      if (state.auth && state.auth.user) {
        const update = (state.auth.user.simulationsRoles || []).filter(function (obj) {
          return obj.simulationId !== payload.simId;
        });
        update.push({roleId: parseInt(payload.roleId), simulationId: payload.simId});
        state.auth.user.simulationsRoles = update;

        if (state.scenarioSelectedId == payload.simId) {
          state.scenarioSelectedRole = payload.roleId;
        }
      }
    },
  },
  getters: {
    getEntity: (state) => (entityId) => {
      return state.simulation.entities[entityId];
    },
    isEntityScenarioChange: (state) => (entityId) => {
      return !!state.simulation.entities[entityId].isScenarioChange;
    },
    getPropertyValue: (state) => (entityId, property) => {
      const entity = state.simulation.entities[entityId];
      if (entity && entity.properties.hasOwnProperty(property)) {
        return entity.properties[property].value;
      }
    },
    isPropertyScenarioChange: (state) => (entityId, property) => {
      const entity = state.simulation.entities[entityId];
      if (entity && entity.properties.hasOwnProperty(property)) {
        return !!entity.properties[property].isScenarioChange;
      }
    },
    getPropertyExtension: (state) => (entityId, property) => {
      const entity = state.simulation.entities[entityId];
      if (entity && entity.properties.hasOwnProperty(property)) {
        return entity.properties[property].extension;
      }
    }
  },
  actions: {
    async authorizeCMD({commit}, call) {
      commit('authorizeCMD', call);
      return await new Promise((resolve, reject) => {
        socket.emit('authorizeCMD', call, (error, payload) => {
          if (error) {
            console.error(error);
            reject(error);
          }
          else {
            resolve(payload);
          }
        });
      });
    },
    async setSelectedEntities({commit}, entityIds) {
      const call = {cmd: 'setSelectedEntities', payload: {entityIds}};
      commit('sendCMD', call);
      return await new Promise((resolve, reject) => {
        socket.emit('cmd', call, (error, payload) => {
          if (error) {
            console.error(error);
            reject(error);
          }
          else {
            resolve(payload);
          }
        });
      });
    },
    async sendCommand({commit}, call) {
      commit('sendCMD', call);
      return await new Promise((resolve, reject) => {
        socket.emit('cmd', call, (error, payload) => {
          if (error) {
            console.error(error);
            reject(error);
          }
          else {
            resolve(payload);
          }
        });
      });
    },
    async callApi({commit}, call) {
      commit('callApi', call);
      return await new Promise((resolve, reject) => {
        socket.emit('callApi', call, (error, payload) => {
          if (error) {
            console.error(error);
            reject(error);
          }
          else {
            resolve(payload);
          }
        });
      });
    },
    async checkToken({dispatch}) {
      const storedToken = storage.isLSAvailable ? localStorage.getItem('authenticationToken') : undefined;
      if (storedToken) {
        console.log('Login via stored token');
        try {
          return await dispatch('login', {method: 'token', token: storedToken});
        } catch (e) {
          console.error(e);
        }
      }
      loginBus.$emit('loginFailed');
    },
    async login({commit, dispatch}, payload) {
      const {method} = payload;
      try {
        commit('login');

        const loginResult = await new Promise(async (resolve, reject) => {
          socket.emit('login', payload, async (error, payload) => {
            try {
              if (error) {
                reject(error);
              }
              else {
                const token = payload.token;
                const user = await dispatch('callApi', {name: 'getUser'});
                commit('loggedId', {user, token});
                commit('setToken', token);

                loginBus.$emit('login');
                resolve({user, token});
              }
            } catch (e) {
              reject(e);
            }
          });
        });
        console.log(`Login method: ${method}`);
        if (method !== 'token') {
          try {
            const simId = await dispatch('getSimId');
            if (!simId) {
              await dispatch('gotoScenarios');
            }
          } catch (e) {
            await dispatch('gotoScenarios');
          }
        }
        return loginResult;
      } catch (e) {
        console.error('Login failed!', e);
        commit('loginFailed', e.message);
        commit('resetToken');
        loginBus.$emit('loginFailed');
        throw e;
      }
    },
    async logout({commit, dispatch}) {
      console.log('logout started');
      try {
        await new Promise((resolve, reject) => {
          socket.emit('logout', (error, payload) => {
            if (error) {
              reject(error);
            }
            else {
              resolve(payload);
            }
          });
        });
        commit('logout');
        commit('resetToken');
        await dispatch('gotoLogin');
      } catch (e) {
        console.error(e);
      }
    },
    async gotoLogin() {
      if (router.history.current.name !== 'Login') {
        await router.push({name: 'Login', query: {redirect: router.history.current.path}});
      }
      else {
        console.log('Stay at login');
      }
    },
    async gotoScenarios() {
      if (router.history.current.name !== 'Controls') {
        await router.push({name: 'Controls'});
      }
      else {
        console.log('Stay at scenarios');
      }
    },
    async getSimId({dispatch}) {
      return dispatch('callApi', {
        name: 'getSimId'
      });
    },

    /**
     * Sends a message to targeted user.<br/>
     * Payload requires an 'id' and 'message' field, optionally also 'awaitResponse' and 'event'<br/>
     * 'id' {Number} - target user's id (id of the user in the database)<br/>
     * 'message' {any} - whatever needs to be sent. socket.io will automatically handle it whether it is a string,
     * number, object...<br/>
     * 'event' {String} - as what event this should be emitted in the socket.io. Defaults to 'userMessage'<br/>
     * 'awaitResponse' {Boolean} - If set to yes, the returned promise will be resolved only after the targeted user
     * responds with any data via callback in the socket.on listener. If set to true, make sure that the client code
     * will respond to this message, otherwise at the BE side there will be a promise hanging forever. If this is set to
     * false, we will never receive the targeted user's response, even if he sends one, instead, the promise will be
     * instantly resolved. Defaults to false.
     * @param context
     * @param payload {{id: number, [message]: any, [event]: string, [awaitResponse]: boolean}}
     * @return {Promise<any>}
     */
    // Use this 'sendToUser' action instead of calling the method below
    // this.store.dispatch('callApi', {name: 'sendToUser', payload: {id: this.to.id, message:
    // this.rtc.localDescription, event: 'rtcOffer'}})
    async sendToUser(context, payload) {
      return await new Promise((resolve, reject) => {
        socket.emit('callApi', {name: 'sendToUser', payload: {id: payload.id, message: payload.message, event: payload.event || 'message', awaitResponse: payload.awaitResponse}}, (error, payload) => {
          if (error) {
            console.error(error);
            reject(error);
          }
          else {
            resolve(payload);
          }
        });
      });
    },

    /**
     * Asks the user to set up their audio stream (give permission to use the microphone) and saves the acquired
     * stream.
     * @param state
     * @return {Promise<MediaStream|null>}
     */
    async setupMicStream({state}) {
      if (!state.micStream) {
        const micStream = await navigator.mediaDevices.getUserMedia({audio: true})
        state.micStream = micStream
        return micStream
      }
      return state.micStream
    },

    /**
     * Shortcut that will automatically craft a message and send it to the targeted user, requesting a call. Before
     * sending, it checks that we are currently able to make the call (we are not ringing someone else or in a call
     * already).
     * @param context
     * @param payload {{to: {id: Number, email: String}}}
     */
    async callUser({state, dispatch}, payload) {
      if (state.call) throw new Error('Already calling (or being called)!')
      if (payload.to.id === state.auth.user.id) throw  new Error(`Can't call yourself!`)
      if (!state.micStream) await dispatch('setupMicStream')
      state.call = new Call(true, payload.to, this)
      await dispatch('sendToUser', {event: 'callInvite', id: payload.to.id})
    },

    /**
     *
     * @param state
     * @param dispatch
     * @param payload {{eventId: number, phoneNumber: number}}
     * @return {Promise<void>}
     */
    async callbackRequest({state, dispatch}, payload) {
      if (state.call) throw new Error('Already calling (or being called)!')
      if (!state.micStream) await dispatch('setupMicStream')
      const call = new Call(true, undefined, this)
      call.request = true
      call.displayName = payload.phoneNumber || '[no phone number specified]'
      call.duration = 0
      state.call = call
      try {
        await dispatch('sendCommand', {cmd: 'callbackRequest', payload: {eventId: payload.eventId, requesterId: state.auth.user.id}})
      }
      catch (e) {
        state.call = null
      }
    },

    async callbackAnswer({state, dispatch, commit}, payload) {
      if (state.call) throw new Error('Already calling (or being called)!')
      if (!state.micStream) await dispatch('setupMicStream')
      const call = new Call(false, payload.to, this)
      call.request = true
      call.displayName = payload.displayName || 'Unknown'
      call.duration = 0
      state.call = call
      try {
        console.log('payload', payload);
        await dispatch('sendCommand', {cmd: 'respondToCallbackRequest', payload: {figurantId: payload.figurantId, callRequestId: payload.callRequestId}})
        state.call.to = payload.to
        state.call.connectRtc()
      }
      catch (e) {
        commit
        state.call = null
      }
    },

    /**
     * If the user is currently in a call it will be cancelled and the other side notified about this.
     * @return {Promise<void>}
     */
    async hangupCall({state, dispatch}) {
      if (!state.call) throw new Error('Cannot hang up when not in a call!')
      if (state.call.request === true && state.call.status !== 'ring') {
        dispatch('sendToUser', {id: state.call.to.id, event: 'declineCall'})
      }
      else if (state.call.request === true && state.call.status === 'ring') {
        // fixme replace with removing request
        // dispatch('sendToUser', {id: state.call.to.id, event: 'declineCall'})
      }
      state.call.hang()
    },


    async acceptCall({state, dispatch}) {
      if (state.call) {
        if (!state.micStream) await dispatch('setupMicStream')
        dispatch('sendToUser', {id: state.call.to.id, event: 'acceptCall'})
        state.call.connectRtc()
      }
    }
  },
  plugins: [socketPlugin, timingPlugin, storagePlugin.plugin]
})
